/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-expressions */
import { BREAKPOINTS } from 'js/constants.js';
import * as pagination from '../../../../../common/filters/pagination.js';
import * as FS from '../../../../../common/filters/filter-shared.js';

const componentName = 'application-list';

class ApplicationList extends HTMLElement {

    // ==========
    // COMPONENT SETUP
    // ==========
    selectors = {
        resultMetadataContainer: `.${componentName}__result-metadata-container`,
        resultMetadata: `.${componentName}__result-metadata`,
        resultDescription: `.${componentName}__result-description`,
    };

    elements = {
        // Filter
        filterDialog: this.querySelector(FS.selectors.dialog),
        filterMobileShow: this.querySelector(FS.selectors.mobileShow),
        filterMobileClose: this.querySelector(FS.selectors.mobileClose),
        filterMobileSubmit: this.querySelector(FS.selectors.mobileSubmit),
        filterForm: this.querySelector(FS.selectors.form),
        filterResets: this.querySelectorAll(FS.selectors.resets),
        filterDetails: this.querySelectorAll(FS.selectors.details),
        facetSearches: this.querySelectorAll(FS.selectors.facetSearches),
        checkboxes: this.querySelectorAll(FS.selectors.checkboxes),
        // Filter chips
        filterMobileHeaderBottom: this.querySelector(FS.selectors.mobileHeaderBottom),
        filterChipsContainers: this.querySelectorAll(FS.selectors.chipsContainers),
        filterChipTemplate: this.querySelector(FS.selectors.chipTemplate),
        // Results
        desktopSortSelect: this.querySelector(FS.selectors.desktopSortSelect),
        resultsContentContainer: this.querySelector(FS.selectors.resultsContentContainer),
        noResults: this.querySelector(`.${componentName}__no-results`),
        results: this.querySelectorAll(`.${componentName}__result`),
        // Pagination
        paginationForm: this.querySelector(FS.selectors.paginationForm),
    };

    customFormProps = Object.freeze({
        search: 'search',
        numPerPage: 'numPerPage',
        sort: 'sort',
    });

    state = {
        matchMobile: window.matchMedia(`(max-width: ${BREAKPOINTS.TABLET}px)`),
        currentPage: 1,
        numPerPage: () => Number(new FormData(this.elements.filterForm).get(this.customFormProps.numPerPage)),
        sort: () => new FormData(this.elements.filterForm).get(this.customFormProps.sort),
    };

    // ==========
    // LOAD CONTENT
    // ==========
    runQuery = (initialLoad = false) => {
        const formData = new FormData(this.elements.filterForm);
        this.updateFilterUi();

        const matchingResults = this.filterResults(formData);
        this.sortResults(matchingResults);

        // compute pagination
        const totalResults = matchingResults.length;
        const firstResultNum = Math.min((this.state.currentPage - 1) * this.state.numPerPage() + 1, totalResults);
        const lastResultNum = Math.min(firstResultNum + this.state.numPerPage() - 1, totalResults);

        // display results page
        const matchingResultsPage = matchingResults.slice(firstResultNum - 1, lastResultNum);
        if (matchingResultsPage.length > 0) {
            this.elements.resultsContentContainer.replaceChildren(...matchingResultsPage);
        } else {
            this.elements.noResults.hidden = false;
            this.elements.resultsContentContainer.replaceChildren(this.elements.noResults);
        }

        // update pagination
        FS.updateResultCounts(this, this.elements.paginationForm, {
            firstResultNum,
            lastResultNum,
            totalResults,
            currentPageNum: this.state.currentPage,
            pageCount: Math.floor(totalResults / this.state.numPerPage()) + 1,
        });

        // update facet counts
        [...this.elements.checkboxes].forEach(this.updateCheckboxCount);

        // hide facets with zero results
        FS.hideFacetsWithNoResults(this.elements.filterDetails);

        if (initialLoad) {
            // determine which filter facets should display search fields
            FS.showFilterSearchFields(this.elements.filterDetails);
        }
    };

    /**
     * @param {FormData} filterParams
     * @returns {HTMLElement[]}
     */
    filterResults = (filterParams) => {
        let matchingResults = [...this.elements.results];

        // Get selected tags
        const customFormPropNames = Object.values(this.customFormProps);
        const filterParamArray = [...new Set(filterParams.keys())]
            .map((key) => [key, filterParams.getAll(key).filter((value) => value !== '')]) // Remove empty filter values
            .filter(([key, values]) => !customFormPropNames.includes(key) && values.length > 0); // Remove custom form props (ones that are not metadata)

        // Get search value
        const searchQuery = filterParams.get(this.customFormProps.search)?.toLowerCase();

        matchingResults = matchingResults.filter((result) => {
            // Cache filterable data if it hasn't been cached already
            if (!result.tagCache || !result.searchCache) {
                const metadataEls = [...result.querySelectorAll(this.selectors.resultMetadata)];

                // Cache the [name, value] pairs of each tag
                result.tagCache = metadataEls.map((metadataEl) => ([metadataEl.dataset.name, metadataEl.dataset.value]));

                // Cache the searchable text: title, description, and tag titles
                const { title } = result.dataset;
                const description = result.querySelector(this.selectors.resultDescription)?.textContent;
                const metadataText = result.querySelector(this.selectors.resultMetadataContainer)?.textContent;

                result.searchCache = [title, description, metadataText]
                    .filter((searchableText) => searchableText != null)
                    .map((searchableText) => searchableText.toLowerCase());
            }

            // For each filter key, some metadata item must match one of the selected values
            const isTagMatch = filterParamArray.every(([selectedKey, selectedValues]) => result.tagCache
                .some(([metadataKey, metadataValue]) => selectedKey === metadataKey && selectedValues.includes(metadataValue)));

            // If a searchQuery is present, then it must be present within the searchable text
            const isSearchMatch = searchQuery == null || searchQuery === ''
                || result.searchCache.some((searchableText) => searchableText.includes(searchQuery));

            return isTagMatch && isSearchMatch;
        });

        return matchingResults;
    };

    /** @param {HTMLElement[]} results */
    sortResults = (results) => {
        switch (this.state.sort()) {
        case 'alphaAsc':
            results.sort((a, b) => new Intl.Collator().compare(a.dataset.title, b.dataset.title));
            break;
        case 'alphaDesc':
            results.sort((a, b) => new Intl.Collator().compare(b.dataset.title, a.dataset.title));
            break;
        case 'pubAsc':
            results.sort((a, b) => new Intl.Collator().compare(a.dataset.title, b.dataset.title));
            results.sort((a, b) => FS.compareDates(a.dataset.date, b.dataset.date));
            break;
        case 'pubDesc':
            results.sort((a, b) => new Intl.Collator().compare(a.dataset.title, b.dataset.title));
            results.sort((a, b) => FS.compareDates(b.dataset.date, a.dataset.date));
            break;
        default:
            // do nothing
        }
    };

    /** @param {HTMLInputElement} checkbox */
    updateCheckboxCount = (checkbox) => {
        const { name, value } = checkbox;

        // compute result count as if this was the only option selected in its category
        const formData = new FormData(this.elements.filterForm);
        formData.set(name, value);
        const resultCount = this.filterResults(formData).length;

        FS.updateFacetItemCount(checkbox, resultCount);
    };

    /**
     * Get pre-selected tags from query parameters and apply the filters on the page.
     * This function also supports comma separated multiple selections.
     * @example
     * `?event=automation-fair`
     */
    readUrlParams = () => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        urlSearchParams.forEach((value, key) => {
            value.split(',').forEach((splitValue) => {
                FS.fillInput([...this.elements.filterForm.elements], key, splitValue, true);
            });
        });
    };

    updateFilterUi = () => {
        // Update chips
        const hasChips = FS.updateChips(
            this.elements.filterForm,
            this.elements.filterChipTemplate,
            this.handleFilterForm,
            this.elements.filterChipsContainers,
            [this.customFormProps.search],
        );

        // Hide/unhide mobile header bottom and reset buttons
        this.elements.filterMobileHeaderBottom.hidden = !hasChips;
        this.elements.filterResets.forEach((el) => {
            el.disabled = !hasChips;
        });

        // Sync desktop sort select
        this.elements.desktopSortSelect.value = new FormData(this.elements.filterForm).get(this.customFormProps.sort);
        this.elements.desktopSortSelect.sync?.();
    };

    // ==========
    // UI LOGIC
    // ==========
    /** @param {SubmitEvent?} ev */
    handleFilterForm = (ev = null) => {
        ev?.preventDefault();
        this.state.currentPage = 1;
        this.runQuery();
    };

    handleMatchMobileChange = () => {
        if (this.state.matchMobile.matches) {
            this.elements.filterDetails.forEach((detailsEl) => {
                detailsEl.open = false;
            });
        } else {
            this.elements.filterDialog.close();
        }
    };

    showMobileFilter = () => FS.showMobileFilter(this.elements.filterDialog);
    closeMobileFilter = () => FS.closeMobileFilter(this.elements.filterDialog);

    /** @param {Event} ev */
    handleDesktopSortSelect = (ev) => {
        FS.fillInput([...this.elements.filterForm.elements], this.customFormProps.sort, ev.currentTarget.value, true);
        this.handleFilterForm();
    };

    handlePagination = pagination.handlePagination({
        handlePrevButton: () => this.state.currentPage -= 1,
        handleNextButton: () => this.state.currentPage += 1,
        handleInput: (input) => this.state.currentPage = input,
        then: this.runQuery,
    });

    // ==========
    // ADD EVENT LISTENERS
    // ==========
    addEventListeners = () => {
        FS.addFacetListeners(this);

        this.elements.filterMobileShow.addEventListener('click', this.showMobileFilter);
        this.elements.filterMobileClose.addEventListener('click', this.closeMobileFilter);
        this.elements.filterMobileSubmit.addEventListener('click', this.closeMobileFilter);

        this.elements.filterForm.addEventListener('submit', this.handleFilterForm);
        this.elements.filterForm.addEventListener('reset', () => window.setTimeout(this.handleFilterForm));
        FS.onFormControlChange(this.elements.filterForm, this.handleFilterForm);

        this.elements.facetSearches.forEach((el) => el.addEventListener('input', FS.handleFacetSearch));
        FS.addViewToggleListeners(this);
        this.elements.desktopSortSelect?.addEventListener('change', this.handleDesktopSortSelect);

        // Pagination
        this.elements.paginationForm.addEventListener('submit', this.handlePagination);

        this.state.matchMobile.addEventListener('change', this.handleMatchMobileChange);
    };

    // ==========
    // INITIALIZE COMPONENT
    // ==========
    connectedCallback() {
        this.readUrlParams();
        this.addEventListeners();
        this.handleMatchMobileChange();
        this.runQuery(true);
        window.sessionListInstance = this; // for debug purposes
    }
}

window.customElements.define(componentName, ApplicationList);
